import { useEffect } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import Shell from "../components/layout/Shell";
import Topbar from "../components/layout/Topbar";
import HomePage from "../features/home/HomePage";
import ArticleDetailPage from "../features/home/ArticleDetailPage";
import ProgramPage from "../features/home/ProgramPage";
import LoginPage from "../features/auth/LoginPage";
import DashboardPage from "../features/dashboard/DashboardPage";
import PeoplePage from "../features/people/PeoplePage";
import CalendarPage from "../features/calendar/CalendarPage";
import SchedulePage from "../features/schedule/SchedulePage";
import AdmissionsPage from "../features/admissions/AdmissionsPage";
import AcademicsPage from "../features/academics/AcademicsPage";
import SectionDetailPage from "../features/academics/SectionDetailPage";
import RoomDetailPage from "../features/academics/RoomDetailPage";
import DirectoryPage from "../features/directory/DirectoryPage";
import AcademicDirectoryPage from "../features/academic-directory/AcademicDirectoryPage";
import GradesPage from "../features/grades/GradesPage";
import TeacherGradeEncodingPage from "../features/grades/TeacherGradeEncodingPage";
import MyGradingPage from "../features/teacher/MyGradingPage";
import MyAttendancePage from "../features/teacher/MyAttendancePage";
import MyStudentsPage from "../features/teacher/MyStudentsPage";
import ClinicStudentsPage from "../features/clinic/ClinicStudentsPage";
import ITLabPage from "../features/itlab/ITLabPage";
import SchoolsPage from "../features/schools/SchoolsPage";
import SchoolsOverviewPage from "../features/schools/SchoolsOverviewPage";
import TeacherAttendancePage from "../features/attendance/TeacherAttendancePage";
import TuitionAdminPage from "../features/tuition/TuitionAdminPage";
import ExpensesPage from "../features/expenses/ExpensesPage";
import TuitionPage from "../features/tuition/TuitionPage";
import StatementOfAccountPage from "../features/tuition/StatementOfAccountPage";
import BooksAdminPage from "../features/books/BooksAdminPage";
import BooksPage from "../features/books/BooksPage";
import AnnouncementsPage from "../features/announcements/AnnouncementsPage";
import ArticlesPage from "../features/articles/ArticlesPage";
import ProgramImagesPage from "../features/programimages/ProgramImagesPage";
import FacilitiesAdminPage from "../features/facilities/FacilitiesAdminPage";
import SettingsPage from "../features/settings/SettingsPage";
import ReportsPage from "../features/reports/ReportsPage";
import SiteContentPage from "../features/sitecontent/SiteContentPage";
import HelpPage from "../features/help/HelpPage";
import SalaryPage           from "../features/salary/SalaryPage";
import TimesheetPage        from "../features/timesheet/TimesheetPage";
import PayrollCalendarPage  from "../features/payrollcalendar/CalendarPage";
import MyPayrollPage        from "../features/mypayroll/MyPayrollPage";
import MyTimesheetPage      from "../features/mypayroll/MyTimesheetPage";
import MyCalendarPage       from "../features/mypayroll/MyCalendarPage";
import { usePayrollEnabled } from "../utils/payrollFeature";
import { useHomepageEnabled } from "../utils/homepageFeature";

/**
 * Fixed desktop width for the authenticated shell is controlled by the
 * --app-fixed-width CSS variable in src/styles/globals.css. This reads it
 * at runtime so there is only one place to edit.
 */
function readFixedWidth() {
  if (typeof window === "undefined") return 1280;
  const raw = getComputedStyle(document.documentElement)
    .getPropertyValue("--app-fixed-width")
    .trim();
  const n = parseInt(raw, 10);
  return Number.isFinite(n) && n > 0 ? n : 1280;
}

function getUser() {
  try {
    return JSON.parse(sessionStorage.getItem("user") || localStorage.getItem("user") || "null");
  } catch { return null; }
}

function RequireAuth({ children }) {
  const user = getUser();
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

/** SuperDev (the project developer) is the highest-privilege role and
 *  must pass every guard a SuperAdmin would. Centralising the check here
 *  means we never forget to add it to a route — every gate below just
 *  short-circuits if this returns true. */
function isSuperDev(user) {
  return user?.role === "SuperDev";
}

function RequireAdmin({ children }) {
  const user = getUser();
  if (!user) return <Navigate to="/login" replace />;
  if (isSuperDev(user)) return children;
  if (user.role !== "Admin" && user.role !== "SuperAdmin") return <Navigate to="/dashboard" replace />;
  return children;
}

function RequireSuperAdmin({ children }) {
  const user = getUser();
  if (!user) return <Navigate to="/login" replace />;
  if (isSuperDev(user)) return children;
  if (user.role !== "SuperAdmin") return <Navigate to="/dashboard" replace />;
  return children;
}

/** Website content (homepage, about, programs, news, contact, footer, media
 *  library) is owned by the ContentManager role. SuperAdmin keeps god-mode
 *  URL access but the menu entry only renders for ContentManager. SuperDev
 *  inherits SuperAdmin's god-mode here too. */
function RequireContentManager({ children }) {
  const user = getUser();
  if (!user) return <Navigate to="/login" replace />;
  if (isSuperDev(user)) return children;
  if (user.role !== "ContentManager" && user.role !== "SuperAdmin") {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

/** Allows the route only if the user's role is in the given allowlist.
 *  SuperDev is implicitly allowed through every gate — they're the
 *  highest-privilege role and must be able to debug / audit every
 *  page the SuperAdmin can reach. */
export function RequireRole({ roles, children }) {
  const user = getUser();
  if (!user) return <Navigate to="/login" replace />;
  if (isSuperDev(user)) return children;
  if (!roles.includes(user.role)) return <Navigate to="/dashboard" replace />;
  return children;
}

/** Gate for the optional Payroll module. Requires SuperAdmin AND the
 *  Settings → Maintenance toggle to be ON. When OFF the route silently
 *  redirects to /dashboard so the URL is undiscoverable. The toggle's
 *  in-memory cache is shared with the topbar gate so flipping it in
 *  Settings updates both at once without a reload. */
function RequirePayrollModule({ children }) {
  const user = getUser();
  const payrollOn = usePayrollEnabled();
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "SuperAdmin" && !isSuperDev(user)) return <Navigate to="/dashboard" replace />;
  if (!payrollOn) return <Navigate to="/dashboard" replace />;
  return children;
}

/** Gate for the public-facing routes (`/`, `/news/:id`,
 *  `/programs/:slug`). When the SuperAdmin flips
 *  Settings → Maintenance → "Enable public homepage" to OFF, the
 *  route silently redirects to `/login` so the site behaves like a
 *  members-only system — no public landing page, no shareable news
 *  / program URLs.
 *
 *  IMPORTANT: when the homepage is disabled and a logged-in user
 *  hits `/`, we send them to `/dashboard` instead of `/login` so
 *  they don't get bounced into a sign-in screen they're already
 *  signed in for. Same pattern the LoginPage's "already signed in"
 *  fallback uses.
 *
 *  Default behaviour is "homepage ON" so existing installs keep the
 *  marketing landing page; only an explicit `homepage.enabled=false`
 *  flips this guard. See utils/homepageFeature.js for the polarity. */
function RequirePublicHomepage({ children }) {
  const homepageOn = useHomepageEnabled();
  if (homepageOn) return children;
  const user = getUser();
  return <Navigate to={user ? "/dashboard" : "/login"} replace />;
}

/** Lighter guard for the per-staff "My Workspace" pages (My Payroll,
 *  My Timesheet, My Calendar). Same payroll.enabled check as the
 *  admin guard, but does NOT require SuperAdmin — every logged-in
 *  staff member can view their own surfaces while the module is on.
 *  When the module is OFF the route redirects to /dashboard so the
 *  URL is undiscoverable, matching the Topbar gate that hides the
 *  "My Workspace" dropdown in the same state. */
function RequireMyWorkspace({ children }) {
  const user = getUser();
  const payrollOn = usePayrollEnabled();
  if (!user) return <Navigate to="/login" replace />;
  if (!payrollOn) return <Navigate to="/dashboard" replace />;
  return children;
}

export default function App() {
  const location = useLocation();
  const isHome = location.pathname === "/"
              || location.pathname.startsWith("/news/")
              || location.pathname.startsWith("/programs/");
  const isLogin = location.pathname === "/login";
  const isPublic = isHome || isLogin;

  // Swap the viewport meta tag so mobile browsers render the authenticated
  // shell at fixed desktop width (pan/zoom), while public pages stay responsive.
  useEffect(() => {
    const meta = document.querySelector('meta[name="viewport"]');
    if (!meta) return;
    meta.setAttribute(
      "content",
      isPublic
        ? "width=device-width, initial-scale=1"
        : `width=${readFixedWidth()}, initial-scale=1`
    );
  }, [isPublic]);

  return (
    <div className={isHome ? undefined : "page"}>
      <Routes>
        {/* Public — gated by RequirePublicHomepage. When the SuperAdmin
            disables the public homepage from Settings, all three of
            these routes redirect to /login (or /dashboard for an
            already-signed-in user) so the site reads as
            members-only. /login itself is never gated — that would
            create a route loop with nowhere to recover to. */}
        <Route path="/" element={
          <RequirePublicHomepage><HomePage /></RequirePublicHomepage>
        } />
        <Route path="/news/:id" element={
          <RequirePublicHomepage><ArticleDetailPage /></RequirePublicHomepage>
        } />
        <Route path="/programs/:slug" element={
          <RequirePublicHomepage><ProgramPage /></RequirePublicHomepage>
        } />
        <Route path="/login" element={<LoginPage />} />

        {/* Protected — with shell + topbar */}
        <Route path="/*" element={
          <RequireAuth>
            <Shell>
              <Topbar />
              <Routes>
                <Route path="/dashboard" element={<DashboardPage />} />
                <Route path="/people"    element={<PeoplePage />} />
                <Route path="/calendar"  element={<CalendarPage />} />
                <Route path="/schedule"  element={<SchedulePage />} />
                <Route path="/admissions/new" element={
                  <RequireRole roles={["Admin", "SuperAdmin", "Registrar", "RegistrarStaff", "AcademicHead"]}>
                    <AdmissionsPage />
                  </RequireRole>
                } />
                {/* Academics is writable by Admin and Academic Head. The
                    Academic Head's whole job is assigning teachers to subjects
                    and scheduling classes. */}
                <Route path="/academics" element={
                  <RequireRole roles={["Admin", "SuperAdmin", "AcademicHead"]}>
                    <AcademicsPage />
                  </RequireRole>
                } />
                {/* Standalone section hub — reached from any section card via
                    "Open full page →". Same role gate as the parent
                    AcademicsPage since it surfaces the same scope of data. */}
                <Route path="/academics/sections/:id" element={
                  <RequireRole roles={["Admin", "SuperAdmin", "AcademicHead"]}>
                    <SectionDetailPage />
                  </RequireRole>
                } />
                {/* Standalone room hub — lists every class scheduled there
                    plus sections using it as homeroom. Same role gate. */}
                <Route path="/academics/rooms/:id" element={
                  <RequireRole roles={["Admin", "SuperAdmin", "AcademicHead"]}>
                    <RoomDetailPage />
                  </RequireRole>
                } />
                {/* Student/parent records are co-owned by Admins and the
                    Registrar office — the Registrar is the day-to-day keeper
                    of enrolment data. */}
                <Route path="/directory" element={
                  <RequireRole roles={["Admin", "SuperAdmin", "Registrar", "RegistrarStaff"]}>
                    <DirectoryPage />
                  </RequireRole>
                } />
                <Route path="/academic-directory" element={<AcademicDirectoryPage />} />
                <Route path="/grades" element={
                  <RequireRole roles={["Student", "Parent", "Admin"]}><GradesPage /></RequireRole>
                } />
                <Route path="/teacher/grades/:id" element={
                  <RequireRole roles={["Teacher", "Admin"]}><TeacherGradeEncodingPage /></RequireRole>
                } />
                <Route path="/teacher/attendance/:id" element={
                  <RequireRole roles={["Teacher", "Admin"]}><TeacherAttendancePage /></RequireRole>
                } />
                <Route path="/my-attendance" element={
                  <RequireRole roles={["Teacher", "Admin"]}><MyAttendancePage /></RequireRole>
                } />
                <Route path="/my-grading" element={
                  <RequireRole roles={["Teacher", "Admin"]}><MyGradingPage /></RequireRole>
                } />
                <Route path="/my-students" element={
                  <RequireRole roles={["Teacher", "Admin"]}><MyStudentsPage /></RequireRole>
                } />
                <Route path="/itlab" element={
                  <RequireRole roles={["ComputerLab", "ComputerLabStaff", "Admin", "SuperAdmin"]}>
                    <ITLabPage />
                  </RequireRole>
                } />
                {/* Schools / branches catalog — CRUD restricted to
                    SuperDev. Empty roles list forces the SuperDev
                    bypass in RequireRole to be the only path that
                    lands here. SuperAdmin / Owner can READ the
                    schools list via the topbar pill + Overview page,
                    but cannot reach this management surface. */}
                <Route path="/schools" element={
                  <RequireRole roles={[]}>
                    <SchoolsPage />
                  </RequireRole>
                } />
                {/* Chain-wide "all schools at a glance" dashboard.
                    Read-tier visibility — SuperDev / SuperAdmin /
                    Owner. The backend gate requires the same set;
                    branch users get bounced back to /dashboard. */}
                <Route path="/schools/overview" element={
                  <RequireRole roles={["SuperAdmin", "Owner"]}>
                    <SchoolsOverviewPage />
                  </RequireRole>
                } />
                <Route path="/clinic/students" element={
                  <RequireRole roles={["Nurse", "ClinicStaff", "Admin", "SuperAdmin"]}>
                    <ClinicStudentsPage />
                  </RequireRole>
                } />
                <Route path="/admin/tuition" element={
                  <RequireRole roles={["Admin", "SuperAdmin", "Cashier", "CashierStaff"]}>
                    <TuitionAdminPage />
                  </RequireRole>
                } />
                {/* Manual expense ledger — same role set as Tuition since
                    Cashier/CashierStaff record day-to-day entries, but
                    edit/void inside the page itself is further restricted
                    to Admin/SuperAdmin (enforced server-side too). */}
                <Route path="/admin/expenses" element={
                  <RequireRole roles={["Admin", "SuperAdmin", "Cashier", "CashierStaff"]}>
                    <ExpensesPage />
                  </RequireRole>
                } />
                <Route path="/tuition" element={
                  <RequireRole roles={["Student", "Parent", "Admin"]}><TuitionPage /></RequireRole>
                } />
                <Route path="/tuition/soa" element={
                  <RequireRole roles={["Student", "Parent", "Admin", "SuperAdmin", "Cashier", "CashierStaff"]}>
                    <StatementOfAccountPage />
                  </RequireRole>
                } />
                <Route path="/admin/books" element={
                  <RequireAdmin><BooksAdminPage /></RequireAdmin>
                } />
                <Route path="/books" element={
                  <RequireRole roles={["Student", "Parent", "Admin"]}><BooksPage /></RequireRole>
                } />
                <Route path="/announcements" element={<AnnouncementsPage />} />
                <Route path="/articles" element={<ArticlesPage />} />
                <Route path="/admin/program-images" element={
                  <RequireContentManager><ProgramImagesPage /></RequireContentManager>
                } />
                <Route path="/admin/site-content" element={
                  <Navigate to="/admin/site-content/homepage" replace />
                } />
                <Route path="/admin/site-content/:section" element={
                  <RequireContentManager><SiteContentPage /></RequireContentManager>
                } />
                <Route path="/admin/facilities" element={
                  <RequireAdmin><FacilitiesAdminPage /></RequireAdmin>
                } />
                <Route path="/reports" element={
                  <RequireAdmin><ReportsPage /></RequireAdmin>
                } />
                {/* Admin + SuperAdmin both open Settings. The page itself
                    hides tabs whose backend is SuperAdmin-only (Users,
                    Maintenance), so Admin lands on the tabs they can
                    actually mutate. /settings/users is still gated tight
                    because that tab IS SuperAdmin-only. */}
                <Route path="/settings" element={
                  <RequireAdmin><SettingsPage /></RequireAdmin>
                } />
                <Route path="/settings/users" element={
                  <RequireSuperAdmin><SettingsPage /></RequireSuperAdmin>
                } />
                {/* Help page — open to every authenticated role. The page
                    itself picks the right guide set per role. */}
                <Route path="/help" element={<HelpPage />} />
                {/* Optional Payroll module (Phase 1). Toggled on/off from
                    Settings → Maintenance. RequirePayrollModule checks the
                    SuperAdmin role AND the live `payroll.enabled` flag —
                    when OFF, /salary, /timesheet, /payroll-calendar
                    redirect to /dashboard so the URL is undiscoverable. */}
                <Route path="/salary" element={
                  <RequirePayrollModule><SalaryPage /></RequirePayrollModule>
                } />
                <Route path="/timesheet" element={
                  <RequirePayrollModule><TimesheetPage /></RequirePayrollModule>
                } />
                <Route path="/payroll-calendar" element={
                  <RequirePayrollModule><PayrollCalendarPage /></RequirePayrollModule>
                } />
                {/* My Workspace — personal HR pages every employee can use.
                    Gated by RequireMyWorkspace so disabling the Payroll
                    module from Settings makes these URLs undiscoverable
                    too (matches the Topbar dropdown gate). Each page
                    still renders its own empty-state when the account
                    isn't linked to an employee record (e.g. Student /
                    Parent). */}
                <Route path="/my-payroll"   element={
                  <RequireMyWorkspace><MyPayrollPage /></RequireMyWorkspace>
                } />
                <Route path="/my-timesheet" element={
                  <RequireMyWorkspace><MyTimesheetPage /></RequireMyWorkspace>
                } />
                <Route path="/my-calendar"  element={
                  <RequireMyWorkspace><MyCalendarPage /></RequireMyWorkspace>
                } />
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
                <Route path="*" element={<Navigate to="/dashboard" replace />} />
              </Routes>
            </Shell>
          </RequireAuth>
        } />
      </Routes>
    </div>
  );
}
